import { useEffect, useState } from 'react';
import { FLOATING_PANEL_HOST_ID } from '@/features/notifications/lib/floatingPanelHost';

/**
 * 420px of docked cart plus 400px of still-readable table.
 *
 * ⚠️ Mirrors the `@container shell-main (min-width: 820px)` rule in `globals.css` — change both
 * together. Layout lives in CSS so it cannot jitter mid-resize; this constant exists only so the
 * cart can decide whether to open itself, which CSS cannot do.
 *
 * Measured against the CONTENT COLUMN, never the viewport: at 1366px that column is 1298 / 1034 /
 * 938px depending on which shell panel is out, so a viewport-keyed check would dock the cart in
 * states with no room for it.
 */
export const DOCK_MIN_WORKSPACE = 820;

/** Whether the workspace is wide enough to show the cart beside the table rather than over it. */
export function useDockFits(): boolean {
  const [fits, setFits] = useState(false);

  useEffect(() => {
    const host = document.getElementById(FLOATING_PANEL_HOST_ID);
    if (!host) return;

    const update = () => setFits(host.clientWidth >= DOCK_MIN_WORKSPACE);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return fits;
}

/**
 * The workspace column the cart and the dialogs anchor to.
 *
 * Resolved in an effect rather than during render: on the first render pass the host may not be in
 * the DOM yet, and reading it in render would cache a null for the life of the component.
 */
export function useWorkspaceHost(): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHost(document.getElementById(FLOATING_PANEL_HOST_ID));
  }, []);

  return host;
}

/**
 * Whether the breadcrumb has scrolled away and the toolbar should carry the way back instead (§6).
 *
 * One threshold with a dead band — yield above 64px, restore below 40px — so the state cannot
 * oscillate. ⚠️ Do NOT replace this with a collapsing sticky header: animating height inside the
 * scroller re-fires scroll, which re-triggers the animation.
 */
export function useCrumbYield(rebindOn: unknown): boolean {
  const [yielded, setYielded] = useState(false);

  useEffect(() => {
    const host = document.getElementById(FLOATING_PANEL_HOST_ID);
    if (!host) return;

    /*
      TWO candidates, because which element scrolls depends on the geometry: docked, the page itself
      is the scroller (`.cart-cq-pad`); floating, it is `main`. Listening to only one means the
      breadcrumb never yields in the other mode.
    */
    const scrollers = [host.querySelector('main'), host.querySelector('.cart-cq-pad')].filter(
      (el): el is HTMLElement => el instanceof HTMLElement,
    );
    if (scrollers.length === 0) return;

    // Latched outside React state so a scroll burst reads the committed value, not a stale render.
    let isYielded = false;

    const onScroll = () => {
      const y = Math.max(...scrollers.map((el) => el.scrollTop));
      if (!isYielded && y > 64) {
        isYielded = true;
        setYielded(true);
      } else if (isYielded && y < 40) {
        isYielded = false;
        setYielded(false);
      }
    };

    onScroll();
    for (const el of scrollers) el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      for (const el of scrollers) el.removeEventListener('scroll', onScroll);
    };
    // Re-bind when the geometry changes: `.cart-cq-pad` only exists while the cart is open, so a
    // listener attached before that never sees the docked scroller.
  }, [rebindOn]);

  return yielded;
}
